import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import EnhancedHeader from '../components/EnhancedHeader';
import '../styles/PrivacyPolicy.css';

const PrivacyPolicy = () => {
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>Privacy Policy | Active Care Residential</title>
        <meta name="description" content="Privacy Policy for Active Care Residential. Learn how we protect your personal data." />
        <meta name="keywords" content="privacy policy, data protection, GDPR" />
      </Helmet>

      {/* Enhanced Header */}
      <EnhancedHeader
        title="Privacy Policy"
        subtitle="How we protect your personal data"
        backgroundImage="/images/residential-care-banner.jpg"
        backgroundGradient="linear-gradient(135deg, rgba(46, 204, 113, 0.7) 0%, rgba(22, 160, 133, 0.7) 100%)"
      />

      {/* Content Section */}
      <motion.section
        className="privacy-page-wrapper"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <div className="privacy-page-section">
          <div className="privacy-page-container">
            <div className="privacy-page-body">
              <section className="privacy-page-item">
                <h2>1. Introduction</h2>
                <p>
                  Active Care Residential (Registration Number: 14464980) is committed to protecting your privacy and ensuring you have a positive experience on our website and when using our services. This Privacy Policy explains how we collect, use, disclose, and otherwise process your personal information.
                </p>
              </section>

              <section className="privacy-page-item">
                <h2>2. Information We Collect</h2>
                <p>We may collect the following types of information:</p>
                <ul className="privacy-page-list">
                  <li><strong>Contact Information:</strong> Name, email address, phone number, postal address</li>
                  <li><strong>Service Information:</strong> Information about care requirements and preferences</li>
                  <li><strong>Communication Data:</strong> Records of communications with us</li>
                  <li><strong>Technical Data:</strong> IP address, browser type, pages visited, time spent on pages</li>
                  <li><strong>Payment Information:</strong> Billing details and financial information</li>
                </ul>
              </section>

              <section className="privacy-page-item">
                <h2>3. How We Use Your Information</h2>
                <p>We use your personal information for:</p>
                <ul className="privacy-page-list">
                  <li>Providing and improving our care services</li>
                  <li>Communicating with you about your care</li>
                  <li>Processing payments and managing accounts</li>
                  <li>Complying with legal and regulatory obligations</li>
                  <li>Marketing and promotional purposes (with your consent)</li>
                  <li>Analyzing website usage and improving user experience</li>
                  <li>Safeguarding and protecting our residents</li>
                </ul>
              </section>

              <section className="privacy-page-item">
                <h2>4. Legal Basis for Processing</h2>
                <p>
                  We process your personal information based on one or more of the following legal grounds:
                </p>
                <ul className="privacy-page-list">
                  <li>Performance of a contract with you</li>
                  <li>Your consent</li>
                  <li>Compliance with legal obligations</li>
                  <li>Protection of vital interests</li>
                  <li>Our legitimate interests</li>
                  <li>Public task</li>
                </ul>
              </section>

              <section className="privacy-page-item">
                <h2>5. Data Sharing</h2>
                <p>
                  We may share your information with trusted third parties including healthcare providers, local authorities, social services, and payment processors. We do not sell your personal data. All recipients of your data are bound by confidentiality agreements.
                </p>
              </section>

              <section className="privacy-page-item">
                <h2>6. Data Retention</h2>
                <p>
                  We retain your personal information only for as long as necessary to fulfill the purposes outlined in this policy, or as required by law. Retention periods vary depending on the type of information and the context of our relationship.
                </p>
              </section>

              <section className="privacy-page-item">
                <h2>7. Your Rights</h2>
                <p>Under UK GDPR and Data Protection Act 2018, you have the right to:</p>
                <ul className="privacy-page-list">
                  <li>Access your personal data (right of access)</li>
                  <li>Rectification of inaccurate data</li>
                  <li>Erasure of your data (right to be forgotten)</li>
                  <li>Restrict processing of your data</li>
                  <li>Data portability</li>
                  <li>Object to processing</li>
                  <li>Lodge a complaint with the ICO</li>
                </ul>
              </section>

              <section className="privacy-page-item">
                <h2>8. Data Security</h2>
                <p>
                  We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These include encryption, secure servers, and access controls.
                </p>
              </section>

              <section className="privacy-page-item">
                <h2>9. International Transfers</h2>
                <p>
                  Your data is primarily stored and processed within the UK. If we transfer data internationally, we ensure adequate safeguards are in place, such as Standard Contractual Clauses.
                </p>
              </section>

              <section className="privacy-page-item">
                <h2>10. Cookies</h2>
                <p>
                  Our website uses cookies to enhance your experience. Please refer to our <a href="/cookie-policy">Cookie Policy</a> for detailed information about how we use cookies and how to manage your cookie preferences.
                </p>
              </section>

              <section className="privacy-page-item">
                <h2>11. Contact Us</h2>
                <p>
                  If you have questions about this Privacy Policy or our privacy practices, please contact us:
                </p>
                <div className="privacy-page-contact-box">
                  <p><strong>Active Care Residential</strong></p>
                  <p>Registration Number: 14464980</p>
                  <p>Email: info@activecareresidential.co.uk</p>
                  <p>Phone: +44 7737 207521</p>
                  <p>Address: East Ham, London</p>
                </div>
              </section>

              <section className="privacy-page-item">
                <h2>12. Changes to This Policy</h2>
                <p>
                  We may update this Privacy Policy periodically. We will notify you of significant changes by email or by posting the updated policy on our website with a revised "Last Updated" date.
                </p>
              </section>
            </div>
          </div>
        </div>
      </motion.section>
    </>
  );
};

export default PrivacyPolicy;